/**
 * Espelho dos ViewModels de `Controllers/TicketController.cs` (SPEC itsm-chamados-fundacao).
 * Fonte da verdade: `openspec/changes/itsm-chamados-fundacao/contrato.md`. Mudou lá, muda aqui.
 *
 * Prioridade NÃO é um enum próprio — é derivada no servidor a partir de Impact × Urgency e chega
 * como `TaskPriority` (1=Low..4=Urgent, mesmo enum de `ProjectTask.ts`). O cliente escolhe impacto
 * e urgência; quem atende pode sobrepor a prioridade calculada (design D7).
 */
import { TaskPriority, TASK_PRIORITY_LABELS } from "./ProjectTask";

export { TaskPriority, TASK_PRIORITY_LABELS };

export enum TicketState {
  Aberto = 1,
  EmAtendimento = 2,
  PendenteCliente = 3,
  Resolvido = 4,
  Fechado = 5,
  Reaberto = 6,
}

export enum TicketChannel {
  Portal = 1,
  Email = 2,
}

export enum TicketImpact {
  UmaPessoa = 1,
  UmaArea = 2,
  Empresa = 3,
}

export enum TicketUrgency {
  PodeEsperar = 1,
  AtrapalhaHoje = 2,
  ParadoAgora = 3,
}

export const TICKET_STATE_LABELS: Record<number, string> = {
  [TicketState.Aberto]: "Aberto",
  [TicketState.EmAtendimento]: "Em atendimento",
  [TicketState.PendenteCliente]: "Pendente do cliente",
  [TicketState.Resolvido]: "Resolvido",
  [TicketState.Fechado]: "Fechado",
  [TicketState.Reaberto]: "Reaberto",
};

export const TICKET_CHANNEL_LABELS: Record<number, string> = {
  [TicketChannel.Portal]: "Portal",
  [TicketChannel.Email]: "E-mail",
};

export const TICKET_IMPACT_LABELS: Record<number, string> = {
  [TicketImpact.UmaPessoa]: "Uma pessoa",
  [TicketImpact.UmaArea]: "Uma área",
  [TicketImpact.Empresa]: "A empresa toda",
};

export const TICKET_URGENCY_LABELS: Record<number, string> = {
  [TicketUrgency.PodeEsperar]: "Pode esperar",
  [TicketUrgency.AtrapalhaHoje]: "Atrapalha hoje",
  [TicketUrgency.ParadoAgora]: "Parado agora",
};

/**
 * Categoria de chamado (`TicketCategoryViewModel`). Catálogo de **dois níveis**: raiz
 * (`parentId: null`) com `children`. Não existe nível 3 — o servidor recusa.
 *
 * O chamado guarda a **folha** escolhida (a subcategoria, ou a própria raiz quando não há
 * subcategoria que sirva); a raiz é derivada do catálogo vigente. Guardar o par
 * (categoria, subcategoria) apodreceria: nada impediria a subcategoria gravada de deixar de
 * pertencer à categoria gravada quando alguém reorganiza o catálogo.
 */
export interface TicketCategory {
  id: number;
  parentId?: number | null;
  name: string;
  /** "Acesso › Reset de senha" — montado no servidor, a tela não remonta. */
  path: string;
  /** A fila cadastrada NESTA categoria. Nula NÃO significa "sem roteamento" — ver `effectiveQueueId`. */
  defaultQueueId?: number | null;
  /** A fila que de fato vai valer, já com a herança da raiz aplicada. É o que a tela deve mostrar. */
  effectiveQueueId?: number | null;
  effectiveQueueName?: string | null;
  flActive: boolean;
  /** Chamados ainda abertos nesta categoria (não soma os das filhas). */
  openTicketCount: number;
  children: TicketCategory[];
}

/** `POST`/`PUT /api/TicketCategory` — `parentId` nulo cria/mantém uma raiz. */
export interface TicketCategoryRequest {
  parentId?: number | null;
  name: string;
  defaultQueueId?: number | null;
}

/** Fila de atendimento (`TicketQueueEntity`). CRUD de fila é `tickets.admin` — fora desta tela. */
export interface TicketQueue {
  id: number;
  name: string;
  description?: string | null;
  flActive: boolean;
  dtCreated: string;
}

/**
 * `TicketViewModel` — visão interna (`tickets.view`+). Traz tudo, inclusive o que o portal do
 * cliente omite (`QueueId`, `AssigneeEmployeeId`, `SlaPaused*`) porque é vocabulário de operação,
 * não de cliente.
 */
export interface Ticket {
  id: number;
  /** Sequencial visível, ex. "2026-0431" — é o que se lê em voz alta, nunca o id (design D5). */
  number: string;
  clientId: number;
  clientName?: string | null;
  requesterClientUserId?: number | null;
  /** `TicketViewModel.RequesterEmail` — não existe `requesterName` no backend. */
  requesterEmail?: string | null;
  queueId?: number | null;
  queueName?: string | null;
  /** A folha classificada. Nulo = não classificado — é legítimo, a triagem corrige. */
  categoryId?: number | null;
  categoryName?: string | null;
  /** "Acesso › Reset de senha", pronto para exibir (design D10). */
  categoryPath?: string | null;
  /** O trabalho mora na tarefa (design D1) — link para abrir/editar direto na tela de Tarefas. */
  projectTaskId: number;
  /** Projeto de atendimento dono da tarefa acima — junto de `projectTaskId`, leva à tela de Tarefas. */
  projectId: number;
  assigneeEmployeeId?: number | null;
  assigneeName?: string | null;
  channel: TicketChannel;
  channelName?: string;
  state: TicketState;
  stateName?: string;
  /**
   * Impacto/urgência/prioridade NÃO têm "*Name" no backend — só `state` e `channel` resolvem nome
   * lá. Rotular impacto/urgência/prioridade é responsabilidade do front, com os mapas de label
   * abaixo (`TICKET_IMPACT_LABELS`, `TICKET_URGENCY_LABELS`, `TASK_PRIORITY_LABELS`).
   */
  impact: TicketImpact;
  urgency: TicketUrgency;
  /** Derivada de impacto × urgência no servidor; quem atende pode ter sobreposto (design D7). */
  priority: TaskPriority;
  subject: string;
  description: string;
  openedAt: string;
  firstResponseAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  reopenCount: number;
  slaResponseDueAt?: string | null;
  slaResolutionDueAt?: string | null;
  slaPausedMinutes: number;
  /** `true` quando o prazo de resolução já passou e o chamado ainda não foi resolvido. */
  slaResolutionBreached: boolean;
  estimatedHours?: number | null;
  workedHours: number;
  /**
   * Para onde este chamado pode ir a partir daqui (`TicketStates.AllowedFrom` no servidor). A tela
   * deve oferecer só estes destinos — a regra continua no servidor, isto só evita oferecer um botão
   * que já se sabe que vai dar 400.
   */
  allowedStates: TicketState[];
  dtCreated: string;
}

/** Filtros de `GET /api/Ticket`. */
export interface TicketListFilter {
  queueId?: number | null;
  state?: TicketState | null;
  clientId?: number | null;
  /** Só os chamados atribuídos ao usuário logado. */
  mine?: boolean;
  /** Mês de abertura "yyyy-MM" (fuso de Brasília, a mesma régua do indicador de SLA). */
  month?: string | null;
  assigneeEmployeeId?: number | null;
  /** Só os sem responsável (ignorado com `assigneeEmployeeId`). */
  unassigned?: boolean;
}

/** `POST /api/Ticket` — abertura em nome de um cliente, feita por quem atende (interno). */
export interface CreateTicketRequest {
  clientId: number;
  queueId?: number | null;
  /** Opcional. Sem `queueId`, é a fila padrão desta categoria que roteia o chamado. */
  categoryId?: number | null;
  subject: string;
  description: string;
  impact: TicketImpact;
  urgency: TicketUrgency;
}

/** `TicketCommentEntity` — thread do chamado. `isInternal=true` nunca é visível ao portal. */
export interface TicketComment {
  id: number;
  ticketId: number;
  authorName?: string | null;
  /** Quem escreveu é da equipe (true) ou o próprio cliente respondendo pelo portal (false). */
  authorIsInternal: boolean;
  body: string;
  /** true = nota interna (só equipe); false = resposta pública (o cliente vê). */
  isInternal: boolean;
  dtCreated: string;
  editedAt?: string | null;
  /** Anexos desta mensagem (`attachments` do `TicketCommentViewModel`). */
  attachments?: TicketAttachment[];
}

/** `TicketAttachmentEntity` — metadados do anexo. Os bytes vêm pelo endpoint de download. */
export interface TicketAttachment {
  id: number;
  ticketId: number;
  /** null = anexo do chamado (abertura); preenchido = anexo de uma mensagem. */
  commentId: number | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  isInternal: boolean;
  uploadedByName: string | null;
  dtCreated: string;
}

export interface CreateTicketCommentRequest {
  body: string;
  isInternal: boolean;
}

/** Uma das duas medidas de SLA (resolução ou primeira resposta). `percent` nulo = sem base para calcular. */
export interface SlaMeasure {
  onTime: number;
  late: number;
  pending: number;
  noSla: number;
  /** NoPrazo / (NoPrazo + Atrasado) * 100; null quando o denominador é 0 — a tela mostra "—". */
  percent: number | null;
}

export interface SlaAssigneeRow {
  /** null = "Sem responsável". */
  employeeId: number | null;
  name: string;
  total: number;
  resolution: SlaMeasure;
  response: SlaMeasure;
}

export interface SlaMonthRow {
  /** "yyyy-MM" */
  month: string;
  total: number;
  resolution: SlaMeasure;
  response: SlaMeasure;
}

/** `GET /Ticket/sla-compliance` e `GET /ClientPortal/tickets/sla-compliance` (portal: byAssignee sempre vazio). */
export interface TicketSlaCompliance {
  month: string | null;
  total: number;
  resolution: SlaMeasure;
  response: SlaMeasure;
  byAssignee: SlaAssigneeRow[];
  byMonth: SlaMonthRow[];
}

export interface TicketSlaFilter {
  /** "yyyy-MM"; null/omitido = todos os meses. */
  month?: string | null;
  assigneeEmployeeId?: number | null;
  clientId?: number | null;
  queueId?: number | null;
  unassigned?: boolean;
}
